<script setup lang="ts">
import {
  disabled,
  image as imageIcon,
  download,
  inbox_in,
} from "pn-design-assets/pn-assets/icons";
import type { Image, ImageCategory } from "@/rvapi";
import { ref, onMounted, computed, watchEffect } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useImageStore } from "@/stores/images";
import type { PnFileUpload } from "@postnord/web-components/components/pn-file-upload";
import type { PnUploadFileItem } from "@postnord/web-components/types/globals/types";
const uploadWidget = ref(null as PnFileUpload | null);
const router = useRouter();
const route = useRoute();
const imageStore = useImageStore();
const uploadError = ref("");
const loading = ref(true);
const base64 = ref("");
const filename = ref("");
const imageId = route.params.id as string;
const image = ref({} as Image);
const categories = ref([] as ImageCategory[]);
const selectedCategory = ref<ImageCategory | undefined>();
const swedishAltText = ref("");
const englishAltText = ref("");
const saving = ref(false);
const isError = ref(false);
const errorDescription = ref("");

const imageUrl = computed(() => {
  if (base64.value) {
    return `data:image/png;base64,${base64.value}`;
  }
  return image?.value?.thumbUrl || "";
});

const getAltText = (language: string) => {
  let description = "";
  if (image?.value?.imageDescription?.length > 0) {
    const imageDescriptionArr = image.value.imageDescription;
    const descriptionObj = imageDescriptionArr.find((imageDescObj) => {
      return imageDescObj.language === language;
    });
    if (descriptionObj) {
      description = descriptionObj.description;
    }
  }
  return description;
};

const filesAdded = (e: any) => {
  if (e.detail.length > 0) {
    const errorFiles = e.detail.filter((d: any) => d.error);

    if (errorFiles.length > 0) {
      errorFiles.forEach((d: any) => {
        e.target.removeFile(d);
      });
    } else {
      uploadError.value = "";
      e.target.startUpload();
    }
  }
};

const uploadFile = async (e: any) => {
  const file: PnUploadFileItem = e.detail.file;
  base64.value = file.base64;
  filename.value = file.filename;
  await uploadWidget.value?.removeFile(file);
};

const fileuploaderror = (e: any) => {
  uploadError.value = e.detail.message.toString();
};

const languageChange = (e: any) => {
  const lang = e.target.id;
  const prevState = image.value.languages.includes(lang);
  if (prevState !== e.target.checked) {
    if (e.target.checked) {
      image.value.languages.push(lang);
    } else {
      image.value.languages = image.value.languages.filter((l) => l !== lang);
    }
  }
};

const canSave = computed(() => {
  return (
    image.value.languages &&
    image.value.languages.length > 0 &&
    imageUrl.value &&
    selectedCategory.value &&
    swedishAltText.value !== "" &&
    englishAltText.value !== ""
  );
});

const save = async () => {
  try {
    resetError();
    saving.value = true;
    if (!image.value) {
      return;
    }
    const uploadObj = {} as any;

    uploadObj.hidden = image.value.hidden;
    uploadObj.languages = image.value.languages;

    if (base64.value) {
      uploadObj.base64 = base64.value;
      uploadObj.filename = filename.value;
    }

    if (image.value.attribution) {
      uploadObj.attribution = image.value.attribution;
      uploadObj.attributionType = image.value.attributionType || "photographer";
    }

    if (image.value.categories) {
      uploadObj.categories = image.value.categories;
    }

    uploadObj.imageDescription = [
      {
        language: "swedish",
        description: swedishAltText.value,
      },
      {
        language: "english",
        description: englishAltText.value,
      },
    ];

    await imageStore.saveImage(imageId, uploadObj);
    router.push("/images");
  } catch (e) {
    console.log(e);
    isError.value = true;
    errorDescription.value = "There was an error in saving the changes.";
    window.scrollTo(0, 0);
  } finally {
    saving.value = false;
  }
};

onMounted(async () => {
  resetError();
  if (uploadWidget.value) {
    uploadWidget.value.addEventListener("filesAdded", filesAdded);
    uploadWidget.value.addEventListener("uploadFile", uploadFile);
    uploadWidget.value.addEventListener("fileuploaderror", fileuploaderror);
  }
  categories.value = await imageStore.getImageCategories();

  if (imageId !== "new") {
    image.value = await imageStore.getImage(imageId);
    swedishAltText.value = getAltText("swedish");
    englishAltText.value = getAltText("english");
    const cat = categories.value.filter(
      (c) => c.categoryId === image.value.categories[0],
    );
    if (cat.length > 0) {
      selectedCategory.value = cat[0];
    }
  } else {
    image.value = {
      languages: ["swedish"],
      hidden: false,
      sortOrder: 1,
      imageUrl: "",
      thumbUrl: "",
      previewUrl: "",
      attribution: "",
      attributionType: "",
      categories: [],
      code: "",
      _id: "",
      imageDescription: [],
    };
  }
  loading.value = false;
});

watchEffect(() => {
  if (uploadWidget.value) {
    uploadWidget.value.addEventListener("filesAdded", filesAdded);
    uploadWidget.value.addEventListener("uploadFile", uploadFile);
    uploadWidget.value.addEventListener("fileuploaderror", fileuploaderror);
  }
});
const resetError = () => {
  isError.value = false;
  errorDescription.value = "";
};
</script>

<template>
  <pn-toast closable="false" appearance="error" v-show="isError">{{
    errorDescription
  }}</pn-toast>
  <h2>Edit image #{{ image?.code }}</h2>
  <div v-show="loading"><pn-spinner size="3" /></div>
  <div v-if="!loading">
    <p>Category</p>
    <select v-model="selectedCategory">
      <option disabled>Select category</option>
      <option v-for="c in categories" :key="c.categoryId" :value="c">
        {{ c.swedish || c.english || c.danish || c.norwegian }}
      </option>
    </select>
    <h3>Visible for language:</h3>
    <p>
      <label for="swedish">
        <pn-checkbox
          checkboxid="swedish"
          :checked="image?.languages && image.languages.includes('swedish')"
          @change="languageChange"
        />
        Swedish
      </label>
      <label for="english">
        <pn-checkbox
          checkboxid="english"
          :checked="image?.languages && image.languages.includes('english')"
          @change="languageChange"
        />
        English
      </label>
      <label for="danish">
        <pn-checkbox
          checkboxid="danish"
          :checked="image?.languages && image.languages.includes('danish')"
          @change="languageChange"
        />
        Danish
      </label>
      <label for="norwegian">
        <pn-checkbox
          checkboxid="norwegian"
          :checked="image?.languages && image.languages.includes('norwegian')"
          @change="languageChange"
        />
        Norwegian
      </label>
    </p>
    <p>
      <label for="hidden">
        <pn-checkbox
          checkboxid="hidden"
          :checked="image.hidden"
          @change="image.hidden = $event.target.checked"
        />
        Completely hide image
      </label>
    </p>

    <h3>Attribution</h3>
    <pn-input v-model="image.attribution" />
    <label for="photographer">
      <pn-radio-button
        radioid="photographer"
        name="attributiontype"
        :value="image.attributionType"
        @change="image.attributionType = 'photographer'"
        :checked="image.attributionType === 'photographer'"
      />
      Photographer
    </label>
    <label for="illustrator">
      <pn-radio-button
        radioid="illustrator"
        name="attributiontype"
        :value="image.attributionType"
        @change="image.attributionType = 'illustrator'"
        :checked="image.attributionType === 'illustrator'"
      />
      Illustrator
    </label>

    <h3>Alternative Text</h3>
    <p>
      <pn-input
        placeholder="Swedish Alternative Text (required *)"
        v-model="swedishAltText"
      ></pn-input>
    </p>
    <p>
      <pn-input
        placeholder="English Alternative Text (required *)"
        v-model="englishAltText"
      ></pn-input>
    </p>

    <h3>Image</h3>

    <div class="grid-two-columns">
      <div class="icon">
        <img v-show="imageUrl" :src="imageUrl" />
        <div v-show="!imageUrl">
          <pn-icon :icon="disabled" class="middle" color="gray400"></pn-icon>
        </div>
      </div>

      <div>
        <pn-file-upload
          ref="uploadWidget"
          accept=".png,.jpg"
          max-size="4mb"
          disabled="false"
          multiple="no"
          show-spinner="true"
        >
          <div class="message">
            <p class="title">
              <pn-icon :icon="imageIcon"></pn-icon>
              <pn-icon :icon="download"></pn-icon>
              <pn-icon :icon="inbox_in"></pn-icon>
            </p>
            <p class="description">
              2539x1807px, png or jpg<br />
              To {{ image.imageUrl ? "Replace" : "Upload" }}, drag image here or
              <a href="#" class="pn-open-file-explorer">browse</a>
            </p>
          </div>
        </pn-file-upload>
        <pn-toast appearance="error" v-show="uploadError">{{
          uploadError
        }}</pn-toast>
      </div>
    </div>
    <div>
      <hr />
      <span v-show="canSave" style="display: block; margin-top: 2rem">
        <pn-button :loading="saving" @click="save">Save changes</pn-button>
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
pn-file-upload {
  .pn-file-upload-container {
    width: 400px;
  }
  pn-icon svg {
    width: 6rem;
    height: 6rem;
  }
}

.icon {
  min-width: 200px;
  min-height: 200px;
  padding: 1rem;

  img {
    border: 1px dashed gray;
    border-radius: 1rem;
    width: 200px;
  }
}
</style>
